<template>
  <v-container fluid>
    <v-row dense no-gutters>
      <v-col class="pa-4">
        <p class="process-repair-tab-section-title">{{$t("pages.partRequest.listTitle")}}</p>
        <main-data-table
        ref="tableRef"
        :headers="getHeaders"
        :inner-headers="innerHeaders"
        :items="items"
        number-pages
        :page.sync="page"
        :size.sync="size"
        :keyword.sync="keyword"
        @fetch="tryToFetchPartRequests"
        :sort-by.sync="sortBy"
        :sort-dir.sync="sortDir"
        :total-elements="totalElements"
        :loading="isLoading"
        innerItemKey="partName">
          <template v-slot:[`header.createdAt`]="{ header }">
              {{$t(header.text, {'0': getTimeZoneInNumber(new Date())})}}
          </template>
          <template v-slot:[`header.updatedAt`]="{ header }">
              {{$t(header.text, {'0': getTimeZoneInNumber(new Date())})}}
          </template>
          <!-- 'updateStatusLoadingKey' var is required for 'v-progress-circular' to vanish when it is done! -->
          <p class="d-none">
            {{updateStatusLoadingKey}}
          </p>

          <template 
          v-if="getRole !== 'CLIENT'"
          v-slot:[`item.clientName`]="{ item }">
              <span>
                  {{item.clientName}}
              </span>
          </template>
          <template v-slot:[`item.createdAt`]="{ item }">
              <span>
                {{getDateNewYorkFormat(item.createdAt)}}
              </span>
          </template>
          <template v-slot:[`item.updatedAt`]="{ item }">
              <span>
                {{getDateNewYorkFormat(item.updatedAt)}}
              </span>
          </template>
          <template v-slot:[`item.status`]="{ item }">
              <h4>
                {{$t(item.statusObj.value)}}
              </h4>
          </template>
          <template v-slot:[`item.details`]="{ item }">
              <route-btn
              label="btns.viewDetails"
              :badge="item.actionRequiredFromCurrentUser ? '1' : null"
              :href="`/process-part-request/${item.id}`"/>
          </template>

          <template v-slot:[`inner-item.imageUrl`]="{ item }">
            <previewable-image
            v-model="item.imageUrl"
            height="40"
            width="40"
            alt="Part Image"
            contain
            class="pa-2"/>
          </template>
          <template v-slot:[`inner-item.category`]="{ item }">
              <span>
                  {{item.category}}
              </span>
          </template>
          <template v-slot:[`inner-item.partName`]="{ item }">
              <span>
                {{item.partName}}
              </span> 
          </template>
          <template v-slot:[`inner-item.quantity`]="{ item }">
              <span>
                {{item.quantity}}
              </span>
          </template>
        </main-data-table>
      </v-col>
    </v-row>

    <export-xlsx-row
    :width="150"
    :height="50"
    :disabled="items.length === 0"
    :loading.sync="isLoading"
    class="px-4 mt-4"
    name="export.title.partRequest"
    :exporter="getExporter"/>
  </v-container>
</template>
<script>
import { execHttpRequestThrowable } from '@/services/http'
import DateUtil from '@/utils/DateUtil'
import MainDataTable from '@/components/common/MainDataTable'
import RouteBtn from '@/components/btns/RouteBtn'
import PreviewableImage from '@/components/common/PreviewableImage'
import ExportXlsxRow from '@/components/common/ExportXlsxRow'
import { exportPartRequestXlsx } from '@/repositories/exportRepo'
import { routeFromRoot } from '@/services/routeService'
import { isLanguageEn, getUserRole } from '@/services/authService'
import { fetchPartRequestByCriteria } from '@/repositories/partRequestRepo'
import { getObjectPartRequestStatusByKey} from '@/utils/statusesUtil'
export default {
  components: {
    MainDataTable,
    RouteBtn,
    ExportXlsxRow,
    PreviewableImage
  },
  data() {
    return {
      headers: [
        {
          text: 'headers.clientName',
          align: 'start',
          sortable: true,
          value: 'clientName',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.creationDateTime',
          align: 'start',
          sortable: true,
          value: 'createdAt', //(yyyy-MM-dd HH:mm)
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.lastChangeDateTime',
          align: 'start',
          sortable: true,
          value: 'updatedAt', //(yyyy-MM-dd HH:mm)
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        },{
          text: 'headers.status',
          align: 'start',
          sortable: true,
          value: 'status',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.details',
          align: 'start',
          sortable: false,
          value: 'details',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }, {
          text: 'headers.parts',
          align: 'start',
          sortable: false,
          value: 'parts',
          permissions: ["ADMIN", 'SUPER_ADMIN', "SUPERVISOR", "SERVICE", "CLIENT", "SUSTAINABILITY_MANAGER"]
        }
      ],
      innerHeaders: [{
          text: 'headers.image',
          align: 'start',
          sortable: false,
          value: 'imageUrl'
        } ,{
          text: 'headers.itemCategory',
          align: 'start',
          sortable: false,
          value: 'category'
        }, {
          text: 'headers.partName',
          align: 'start',
          sortable: false,
          value: 'partName'
        }, {
          text: 'headers.quantity',
          align: 'start',
          sortable: false,
          value: 'quantity'
        },
      ],
      sortBy: "createdAt",
      sortDir: "DESC",
      keyword: "",
      items: [],
      totalElements: 0,
      size: 10,
      page: 1,
      updateStatusLoadingKey: 0,
      isLoading: false
    }
  },
  computed: {
    isEN() {
      return isLanguageEn()
    },
    getRole() {
      return getUserRole()
    },
    getExporter() {
      return async () => await exportPartRequestXlsx(this.keyword, this.getSort)
    },
    getHeaders() {
      return this.headers.filter(o => o.permissions.includes(this.getRole))
    },
    getSort() {
      return `${this.$refs.tableRef.getColumnNameByHeaderValue(this.sortBy)},${this.sortDir}`
    }
  },
  methods: {
    async tryToFetchPartRequests({search, size, page, sort}) {
      this.items = []
      const r = await execHttpRequestThrowable(async () => await fetchPartRequestByCriteria(search, size, page - 1, sort),
      {},
      v => this.isLoading = v)
      this.pageable = r.response.data
      this.totalElements = this.pageable.totalElements
      this.pageable.content.forEach(o => {
        o.statusObj = getObjectPartRequestStatusByKey(o.status, true)
        o.isLoading = false
      })
      this.items = this.pageable.content
    },
    getDateNewYorkFormat(d) {
      return DateUtil.getDateNewYorkFormat(d)
    },
    routeTo(link) {
      routeFromRoot(link)
    },
    getTimeZoneInNumber(d) {
      return DateUtil.getTimeZoneInNumber(d)
    }
  },
  async mounted() {
    // if (this.getRole !== 'CLIENT' && this.getRole !== 'SERVICE') routeFromRoot('active-repairs')
    await this.tryToFetchPartRequests({
      search: this.keyword,
      size: this.size,
      page: this.page,
      sort: this.getSort
    })

    this.$feedback.fill("Part Request List Page", this.$route.path)
  }
}
</script>
<style>
.detail-btn.v-btn.v-btn--is-elevated.v-btn--has-bg.theme--light {
  color: #fff;
  background-color: #4285f4!important;
  border-color: #75a4f0;
  font-family: 'Titillium Web', sans-serif;
  font-weight: 400;
  font-size: 0.65rem;
  letter-spacing: 1px;
}
</style>